import { FilterOutlined } from "@ant-design/icons"
import { Flex, Tag, Typography } from "antd"
import React, { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { neutral } from "../../../../components/widgets/tokens.ts"
import { summarizeReportFilters } from "../../helpers/reportFilterSummary.ts"
import { useReportFilterOptions } from "../../hooks/useReportFilterOptions.ts"
import type { ReportDefinitionV1 } from "../../types/canvas.types.ts"

const { Text } = Typography

interface ReportFiltersSummaryProps {
  definition: ReportDefinitionV1 | null | undefined
  /** One wrapped line, for the builder; rows otherwise. */
  compact?: boolean
}

/** The report's saved default filters, read-only. Edited on the Filters step. */
export const ReportFiltersSummary = ({
  definition,
  compact = false
}: ReportFiltersSummaryProps) => {
  const { t } = useTranslation()
  const { options } = useReportFilterOptions()
  const rows = useMemo(
    () => summarizeReportFilters(definition, options, t),
    [definition, options, t]
  )
  const narrowing = rows.filter(
    (row) => row.key !== "window" && row.key !== "compare"
  )

  if (compact)
    return (
      <Flex
        align="center"
        gap={6}
        wrap="wrap"
        data-testid="report-filters-summary"
        className="text-[13px]"
      >
        <FilterOutlined aria-hidden style={{ color: neutral.muted }} />
        <Text type="secondary">{t("reports.filtersSummary.applied")}</Text>
        {rows.map((row) => (
          <Tag key={row.key} className="m-0" bordered={false}>
            {t(row.labelKey)}: {row.values.join(", ")}
          </Tag>
        ))}
        {narrowing.length === 0 && (
          <Text type="secondary">
            {t("reports.filtersSummary.noneNarrowing")}
          </Text>
        )}
      </Flex>
    )

  return (
    <dl
      className="m-0 flex flex-col gap-3"
      data-testid="report-filters-summary"
    >
      {rows.map((row) => (
        <div key={row.key} className="flex flex-col gap-0.5">
          <dt>
            <Text strong className="text-[13px]">
              {t(row.labelKey)}
            </Text>
          </dt>
          <dd className="m-0">
            <Text type="secondary" className="text-[13px]">
              {row.values.join(", ")}
            </Text>
          </dd>
        </div>
      ))}
      {narrowing.length === 0 && (
        <Text type="secondary" className="text-[13px]">
          {t("reports.filtersSummary.noneNarrowing")}
        </Text>
      )}
    </dl>
  )
}
